import type { identifier } from '@core/models/base';
import { authedAxios, deleteReq, patch, post, uploadFile } from '@shared/axios';
import type { JobDisplayModel } from '@shared/models/jobs/jobModels';
import type { JobDisplayModelV3 } from '@shared/models/jobs/v3/displayModels';
import type { PagedParameters, PagedResult } from '@shared/models/paged.collection';
import type { StudioDisplayModel } from '@shared/models/studios/studios';

export type JobFilter = {
  prepay?: boolean;
  galleries?: boolean;
};
export const getStudioDetails = async (studioId: identifier) =>
  (await authedAxios.get<StudioDisplayModel>(`/api/studioadmin/studios/${studioId}`)).data;

export const getStudioJobs = async (
  studioId: identifier,
  params: PagedParameters,
  filter?: JobFilter,
  organizationId?: identifier,
) =>
  (
    await authedAxios.get<PagedResult<JobDisplayModel>>(
      `/api/studioadmin/studios/${studioId}/organizations/${organizationId}/jobs`,
      {
        params: {
          ...params,
          prepay: filter?.prepay ? 1 : 0,
          galleries: filter?.galleries ? 1 : 0,
        },
      },
    )
  ).data;

export const getJobDisplayModel = async (studioId: identifier, jobId: identifier, organizationId?: identifier) =>
  (
    await authedAxios.get<JobDisplayModel>(
      `/api/studioadmin/studios/${studioId}/organizations/${organizationId}/jobs/${jobId}`,
    )
  ).data;

export const removeStripe = async (studioId: identifier) =>
  await (
    await authedAxios.patch<StudioDisplayModel>(`/api/studioadmin/studios/${studioId}/stripe/remove`)
  ).data;

export const connectStripe = async (studioId: identifier, code: string) =>
  await patch<StudioDisplayModel>(`/api/studioadmin/studios/${studioId}/stripe/connect?code=${code}`);

export const updateStudio = async (studioId: number, model: StudioDisplayModel) => {
  return await patch<StudioDisplayModel>(`/api/studioadmin/studios/${studioId}`, model);
};

export const removeStudioLogo = async (studioId: identifier) => {
  return await deleteReq(`/api/studioadmin/studios/${studioId}/logo`);
};

export const uploadStudioLogo = async (studioId: identifier, file: File) => {
  return await uploadFile<StudioDisplayModel>(`/api/studioadmin/studios/${studioId}/logo`, file, 'PUT');
};

export const requestStudioVerification = async (studioId: identifier, accountNumber: string, password: string) => {
  return await post<StudioDisplayModel>(`/api/studioadmin/studios/${studioId}/request-verification`, {
    accountNumber,
    password,
  });
};

export const archiveJob = async (
  studioId: identifier,
  organizationId: identifier,
  jobId: identifier,
  archived: boolean,
) =>
  await post<JobDisplayModelV3>(
    `/api/v3/studioadmin/studios/${studioId}/organizations/${organizationId}/jobs/${jobId}/archive`,
    { archived },
  );
